import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { push, replace } from 'redux-first-history';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';

import {
    BasicTable,
    Typography,
    PrimaryButton,
    Pagination,
    MobileTable,
    EmptyState,
    Container,
    DateDropdown,
    SimpleLoader,
    FiltersPopover,
    FilterIcon,
    palette,
    BasicTableHeadersProps,
    Header,
} from '@dev/ee-components';
import { convertToLocalTime, safelyDecodeBase64Url } from '@dev/ee-tools';
import styled from 'styled-components';

import { parse } from 'query-string';
import Skeleton from 'react-loading-skeleton';
import dayjs from '@api/services/tools/dayjs';

import UpIcon from '../../upIcon.svg';

import { ShowDialog, HideDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import ExportModal from '../../../../../ExportModal';
import { LinkContactsState } from '@api/store/statistics/types';
import { LoadLinkContactsStart, ExportLinkContacts } from '@api/store/statistics/actions';
import { ExportLinkContactsParams, LinkContactsParams } from '@api/services/ElasticApi';
import SingleChannelProps from '../../SingleChannelInterface';
import { CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';
import { ApplicationState as State } from '@api/store';
import { addParamToUrl } from '@api/services/tools/handleUrlParams';
import { withRouter } from 'react-router-dom';
import { defaultDateDropdownOption, DateDropdownOptions, getDateDropdownOptionsConfig, getDateRangeData } from '../config';
import { ManipulateType } from 'dayjs';
import { getDateDropdownLabel } from '@api/services/tools/getDateDropdownValue';
import { StatisticsPaths, pathsParams } from '@api/services/dictionaries/paths';

const mapState = (state: State, ownProps: SingleChannelProps): SingleChannelProps => {
    return {
        ...ownProps,
        browser: state.browser,
        channels: state.channels,
        statistics: state.statistics,
    };
};

const dispatchProps = {
    LoadLinkContactsStart,
    ExportLinkContacts,
    ShowDialog,
    HideDialog,
    Push: push,
    Replace: replace,
};

type DispatchProps = {
    LoadLinkContactsStart(params: LinkContactsParams);
    ExportLinkContacts(params: ExportLinkContactsParams);
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    Push(url: string): void;
    Replace(param: Record<string, string> | string): void;
};

type Props = DispatchProps & SingleChannelProps;

const mobilePadding = {
    left: 16,
    right: 16,
};

const Content = styled.div<{ isMobile: boolean }>`
    padding: 30px 0px;
    background-color: #fff;
    margin-top: ${(props): string => (props.isMobile ? '16px' : '33px')};
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
`;

const TableContainer = styled.div`
    & .MuiTableBody-root > tr:first-child {
        & th,
        td {
            border-top: none;
        }
    }
`;

const EmptyStateWrapper = styled.div`
    margin-top: 16px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 96px;
    width: 100%;
`;

const ActionsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
`;

const MobileTableContainer = styled.div`
    margin-top: 24px;
`;

const FiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    padding: 11px 11px;
    right: 16px;
    position: absolute;
`;

const DATE_FORMAT = 'YYYY-MM-DD';

const DateRangeContainer = styled.div`
    display: flex;
    margin-left: 37px;
    margin-bottom: 30px;
`;

const HelpFont = styled<any>(Typography)`
    font-size: 11px;
`;
interface DateRangeValues {
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
}

interface QueryValues {
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
    daterange: DateDropdownOptions;
}

class ChannelLinkContacts extends Component<Props, LinkContactsState> {
    constructor(props) {
        super(props);

        const { match } = this.props;
        const { from, to, daterange } = this.getQueryValues();
        const { from: fromDefault, to: toDefault } = this.getDateRangeValues(defaultDateDropdownOption);
        const channelName = safelyDecodeBase64Url(match.params.channelName);
        const query: any = parse(location.search);
        const link = safelyDecodeBase64Url(query?.link);

        const state: LinkContactsState = {
            exportOptions: {
                from: null,
                to: null,
                channelName,
                fileName: null,
                compressionFormat: CompressionFormat.Zip,
                fileFormat: ExportFileFormats.Csv,
            },
            from: from ?? fromDefault,
            to: to ?? toDefault,
            dateRangeValue: daterange,
            link,
        };
        this.state = state;
    }

    private getQueryValues(): QueryValues {
        const { location } = this.props;
        const query = parse(location.search);
        const dateDropdownOptionsConfig = getDateDropdownOptionsConfig();
        const from = query.from && dayjs(query.from as string).startOf('day');
        const to = query.to && dayjs(query.to as string).endOf('day');

        return {
            from,
            to,
            daterange: dateDropdownOptionsConfig[query.daterange as DateDropdownOptions]?.value || defaultDateDropdownOption,
        };
    }

    private getDateRangeValues(option: DateDropdownOptions): DateRangeValues {
        const dateDropdownConfig = getDateDropdownOptionsConfig();
        const to = dayjs().endOf('day');
        const { valueInUnit, unit } = dateDropdownConfig[option];
        const from = dayjs()
            .subtract(valueInUnit, unit as ManipulateType)
            .startOf('day');
        return { from, to };
    }

    public componentDidMount(): void {
        this.loadLinkContacts();
    }

    private fileNameLinkContactsRef = React.createRef<HTMLInputElement>();

    private loadLinkContacts(): void {
        const { LoadLinkContactsStart, match, location } = this.props;
        const query: any = parse(location.search);
        const params: LinkContactsParams = {
            channelName: safelyDecodeBase64Url(match.params.channelName),
            from: this.state.from.utc().startOf('day').toJSON(),
            to: this.state.to.utc().endOf('day').toJSON(),
            link: safelyDecodeBase64Url(query.link),
        };

        if (query.page) {
            params.linkContactsActivePage = parseInt(query.page, 10);
        }

        if (query.perPage) {
            params.perPage = parseInt(query.perPage, 10);
        }

        LoadLinkContactsStart(params);
    }

    private renderNavigation(): JSX.Element {
        const { match, t, statistics } = this.props;
        const isMobile = this.isMobile();
        const query = parse(location.search);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();

        return (
            <div>
                <Header
                    isMobile={isMobile}
                    header={{ title: this.state.link }}
                    breadcrumbs={{
                        text: match.params.channelName ? safelyDecodeBase64Url(match.params.channelName) : t('activity'),
                        onClick: this.navigateToChannel.bind(this),
                    }}
                    customContent={
                        <>
                            {!isMobile && (
                                <PrimaryButton
                                    id="exportlinks"
                                    onClick={this.openExportLinksModal.bind(this)}
                                    disabled={statistics.totalLinkContactsCount ? false : true}
                                    size="medium"
                                    text={t('exportcontacts')}
                                    variant="text"
                                    startIcon={<UpIcon />}
                                />
                            )}

                            {isMobile && (
                                <ActionsContainer>
                                    <PrimaryButton
                                        id="exportlinks"
                                        onClick={this.openExportLinksModal.bind(this)}
                                        disabled={statistics.totalLinkContactsCount ? false : true}
                                        size="medium"
                                        text={t('exportcontacts')}
                                        variant="text"
                                        startIcon={<UpIcon />}
                                    />
                                    <FiltersContainer>
                                        <FiltersPopover
                                            onApply={(obj, from, to): void => {
                                                this.mobileFiltersApply(obj, from, to);
                                            }}
                                            tabs={[
                                                {
                                                    label: t('daterange'),
                                                    defaultValue: getDateDropdownLabel(DateDropdownOptions.Last30days),
                                                    options: getDateRangeData(true),
                                                    type: 'date',
                                                    startDate,
                                                    endDate,
                                                },
                                            ]}
                                            title={t('daterange')}
                                            translations={{
                                                apply: t('apply'),
                                                close: t('close'),
                                            }}
                                        >
                                            <FilterIcon color={palette.gray[6]} width={20} height={20} />
                                        </FiltersPopover>
                                    </FiltersContainer>
                                </ActionsContainer>
                            )}
                        </>
                    }
                    customTitleColumnSize={10}
                />
            </div>
        );
    }

    private navigateToChannel(): void {
        const { match, Push } = this.props;
        const { channelName } = match?.params || {};
        if (!channelName) {
            return Push(StatisticsPaths.AllLinkTracking);
        }
        Push(StatisticsPaths.StatisticsChannelOverview.replace(pathsParams.channelName, match.params.channelName));
    }

    private mobileFiltersApply(filtersObj, newFrom, newTo): void {
        const dateValue = filtersObj[0] as DateDropdownOptions;
        let from;
        let to;

        if (dateValue === DateDropdownOptions.Calendar) {
            from = dayjs(newFrom).startOf('day');
            to = dayjs(newTo).endOf('day');
        } else {
            const { from: fromDate, to: toDate } = this.getDateRangeValues(dateValue);
            from = fromDate;
            to = toDate;
        }

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT), daterange: dateValue, page: 1 });
        this.setState({ from, to, dateRangeValue: dateValue }, (): void => {
            this.loadLinkContacts();
        });
    }

    private renderTable(): JSX.Element {
        const headers = this.getHeaders();
        const { statistics, t } = this.props;
        const { linkContactsPerPage } = statistics;

        const SkeletonTemplate = {
            email: <Skeleton />,
            clicked: <Skeleton />,
        };
        const SkeletonRows = [...new Array(linkContactsPerPage)].map((_) => SkeletonTemplate);

        return (
            <BasicTable
                rows={statistics.linkContactsLoading ? SkeletonRows : this.getRows()}
                headers={headers}
                isEmpty={!statistics?.linkContactsLoading && !statistics.totalLinkContactsCount}
                emptyStateProps={{ text: t('thereisnodatatodisplay') }}
            />
        );
    }

    private getHeaders(): BasicTableHeadersProps[] {
        const { t } = this.props;

        return [
            { id: 'email', disablePadding: true, label: t('trackedlink') },
            { id: 'clicked', disablePadding: false, label: t('lastclicked'), columnAlign: 'right' },
        ];
    }

    private getRows(): any[] {
        const {
            statistics: { linkContactsList },
            statistics,
        } = this.props;

        if (!statistics.totalLinkContactsCount || !linkContactsList?.Contacts) {
            return [];
        }

        return linkContactsList?.Contacts?.map((contact) => {
            return {
                id: contact.Email,
                email: <Typography variant="caption">{contact.Email}</Typography>,
                clicked: (
                    <div>
                        <Typography variant="caption" style={{ marginBottom: 2 }}>
                            {convertToLocalTime(contact.LastClicked, 'L')}
                        </Typography>
                        <HelpFont variant="body1" bodyVariant="help">
                            {convertToLocalTime(contact.LastClicked, 'LT')}
                        </HelpFont>
                    </div>
                ),
                rowID: contact.Email,
            };
        });
    }

    private renderMobileTable(): JSX.Element {
        const header = this.getMobileTableHeaders();
        const rows = this.getMobileTableRows();
        const footer = <></>;
        const { statistics, t } = this.props;

        if (!statistics?.linkContactsLoading && !statistics.totalLinkContactsCount) {
            return (
                <EmptyStateWrapper>
                    <EmptyState background="#fff" text={t('thereisnodatatodisplay')} />
                </EmptyStateWrapper>
            );
        }

        if (statistics?.linkContactsLoading) {
            return this.renderLoadingState();
        }

        return (
            <MobileTableContainer>
                <MobileTable headers={header} rows={rows} footer={footer} expandedRows={{ email: true } as any} />
            </MobileTableContainer>
        );
    }

    private getMobileTableHeaders(): string[] {
        const { t } = this.props;

        return [t('email'), t('clicked')];
    }

    private getMobileTableRows(): any[] {
        const { linkContactsList, linkContactsLoading, linkContactsPerPage } = this.props.statistics;

        if (!linkContactsList) {
            return [];
        }

        const SkeletonTemplate = {
            email: <Skeleton />,
            clicked: <Skeleton />,
        };
        const SkeletonRows = [...new Array(linkContactsPerPage)].map((_) => SkeletonTemplate);

        const rows = linkContactsList?.Contacts.map((contact) => {
            return {
                email: contact.Email,
                clicked: (
                    <div>
                        {convertToLocalTime(contact.LastClicked, 'L')}
                        <br />
                        <span style={{ fontSize: '11px', color: palette.gray[5] }}>{convertToLocalTime(contact.LastClicked, 'LT')}</span>
                    </div>
                ),
            };
        });

        return linkContactsLoading ? SkeletonRows : rows;
    }

    private changePage(page: number, perPage: number): void {
        const { LoadLinkContactsStart, location, match } = this.props;
        const { from, to } = this.state;
        const link = parse(location.search).link as string;

        LoadLinkContactsStart({
            link: safelyDecodeBase64Url(link),
            channelName: safelyDecodeBase64Url(match.params.channelName),
            from: from.utc().startOf('day').format(),
            to: to.utc().endOf('day').format(),
            linkContactsActivePage: page,
            perPage,
        });
    }

    private changeExportFileFormat(data: { value: ExportFileFormats; label: string }): void {
        const { exportOptions } = this.state;

        exportOptions.fileFormat = data.value;

        this.setState({ exportOptions });
    }

    private changeExportCompression(enabled: boolean): void {
        const { exportOptions } = this.state;

        exportOptions.compressionFormat = enabled ? CompressionFormat.Zip : CompressionFormat.None;

        this.setState({ exportOptions });
    }

    private exportLinkContacts(): void {
        const { ExportLinkContacts, location, match } = this.props;
        const { exportOptions, from, to } = this.state;
        const fileName = this.fileNameLinkContactsRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;
        const link = parse(location.search).link as string;

        const exportParams = {
            link: safelyDecodeBase64Url(link),
            channelName: safelyDecodeBase64Url(match.params.channelName),
            from: from.utc().startOf('day').format(),
            to: to.utc().endOf('day').format(),
            fileFormat,
            //limit?: number;
            //offset?: number;
            compressionFormat: exportOptions.compressionFormat,
            fileName,
        };

        ExportLinkContacts(exportParams);
    }

    private openExportLinksModal(): void {
        const { ShowDialog, t } = this.props;
        const { exportOptions } = this.state;

        ShowDialog({
            title: t('exportcontacts'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.fileNameLinkContactsRef}
                    inputValue={exportOptions.fileFormat}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportLinkContacts.bind(this),
        });
    }

    private isMobile(): boolean {
        const { browser } = this.props;
        return browser?.lessThan?.hd;
    }

    private setDatesBasedOnCalendar(fromTime: Date, toTime: Date): void {
        const from = dayjs(fromTime).startOf('day');
        const to = dayjs(toTime).endOf('day');

        this.addSearchQuery({
            from: from.format(DATE_FORMAT),
            to: to.format(DATE_FORMAT),
            daterange: DateDropdownOptions.Calendar,
            page: 1,
        });
        this.setState({ from, to, dateRangeValue: DateDropdownOptions.Calendar }, (): void => {
            this.loadLinkContacts();
        });
    }

    private renderDateRange(): JSX.Element {
        const dateRangeData = getDateRangeData(false);
        let startDate, endDate;
        if (this.state.dateRangeValue === DateDropdownOptions.Calendar) {
            startDate = this.state.from.format();
            endDate = this.state.to.format();
        }
        const dateDropdownValue = getDateDropdownLabel(DateDropdownOptions.Last30days);
        return (
            <DateDropdown
                startDate={startDate}
                endDate={endDate}
                calendarOnChange={this.setDatesBasedOnCalendar.bind(this)}
                onItemClick={(e, obj): void => {
                    const dateOptionValue = obj.value as DateDropdownOptions;
                    const { from, to } = this.getDateRangeValues(dateOptionValue);
                    this.addSearchQuery({
                        from: from.format(DATE_FORMAT),
                        to: to.format(DATE_FORMAT),
                        daterange: dateOptionValue,
                        page: 1,
                    });
                    this.setState({ from, to, dateRangeValue: dateOptionValue }, (): void => {
                        this.loadLinkContacts();
                    });
                }}
                options={dateRangeData}
                returnRawData
                type="input"
                placement="bottom-end"
                buttonVariant="tertiary"
                value={dateDropdownValue}
            />
        );
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private renderFooter(): JSX.Element {
        const {
            statistics: { totalLinkContactsCount, linkContactsActivePage, linkContactsPerPage },
        } = this.props;

        return (
            <Pagination
                dataLength={totalLinkContactsCount}
                perPage={linkContactsPerPage}
                activePage={linkContactsActivePage}
                siblingCount={1}
                boundaryCount={1}
                isRecalculatingActivePage
                handlePaginationChange={this.changePage.bind(this)}
                perPageOptions={[
                    { label: '5', value: 5 },
                    { label: '10', value: 10 },
                    { label: '15', value: 15 },
                    { label: '20', value: 20 },
                    { label: '25', value: 25 },
                ]}
            />
        );
    }

    private renderLoadingState(): JSX.Element {
        const displayContainer = {
            display: 'flex',
            height: 'calc(100vh - 200px)',
        };

        return (
            <Container style={displayContainer}>
                <LoadingWrapper>
                    <SimpleLoader />
                </LoadingWrapper>
            </Container>
        );
    }

    public render(): JSX.Element {
        const { statistics } = this.props;
        const isMobile = this.isMobile();

        if (isMobile) {
            return (
                <Container paddingMobile={mobilePadding}>
                    {this.renderNavigation()}
                    {this.renderMobileTable()}
                    {!statistics.totalLinkContactsCount ? null : this.renderFooter()}
                </Container>
            );
        }

        return (
            <Container paddingMobile={mobilePadding}>
                {this.renderNavigation()}
                <Content isMobile={isMobile}>
                    <DateRangeContainer>{this.renderDateRange()}</DateRangeContainer>
                    <TableContainer>{this.renderTable()}</TableContainer>
                    {!statistics.totalLinkContactsCount ? null : this.renderFooter()}
                </Content>
            </Container>
        );
    }
}

export default compose(
    withRouter,
    withTranslation(),
    connect<SingleChannelProps, DispatchProps>(mapState, dispatchProps)
)(ChannelLinkContacts);
